// src/services/studentService.ts
// UPDATED: 2026-03-27
// ✅ enroll() aceita apenas payload — upload de foto é feito pelo CadastroAlunoForm
// ✅ Removido uploadFotoParaS3 (usava /upload sem signed URL)

import apiClient from './api';

export interface Student {
  id: string;
  userId: string;
  affiliateId: string;
  systemId: string;
  fullName: string;
  email?: string;
  cpf?: string;
  cin?: string;
  phone?: string;
  whatsapp?: string;
  birthdate?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  photoUrl?: string;
  courseName?: string;
  courseWorkload?: number;
  courseStartDate?: string;
  courseEndDate?: string;
  enrollmentDate?: string;
  status: 'active' | 'graduated' | 'suspended' | 'inactive';
  evasionFlag: boolean;
  evasionAffiliates?: Array<{ affiliateId: string; registeredAt: string }>;
  affiliate?: { schoolName: string; logoUrl?: string };
  createdAt: string;
}

export interface EnrollStudentPayload {
  affiliateId: string;
  fullName: string;
  email: string;
  cpf?: string;
  cin?: string;
  phone?: string;
  whatsapp?: string;
  birthdate?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  courseName?: string;
  courseWorkload?: number;
  courseStartDate?: string;
  courseEndDate?: string;
  photoUrl?: string;
}

export interface EnrollStudentResponse {
  id: string;
  systemId: string;
  fullName: string;
  affiliateId: string;
  status: string;
  message: string;
}

export interface Certificate {
  id: string; studentId: string; courseId: string;
  certificateUrl: string; qrCodeUrl: string; createdAt: string;
}

export interface DiplomaRequest {
  id: string; studentId: string;
  status: 'pending' | 'processing' | 'completed' | 'rejected';
  requestType: 'original' | 'second_copy'; createdAt: string;
}

export interface CarteiraDigital {
  id: string; studentId: string; photoUrl: string;
  qrCodeData: string; qrCodeUrl: string;
  status: 'active' | 'inactive' | 'expired';
  issuedAt: string; expiresAt: string;
}

export interface StudentCourse {
  id: string; name: string; description: string;
  affiliateName: string; enrolledAt: string;
  completedAt?: string; certificateAvailable: boolean;
}

export interface StudentStats {
  total: number; active: number; graduated: number;
  suspended: number; evasion: number;
}

class StudentService {
  async enroll(payload: EnrollStudentPayload): Promise<EnrollStudentResponse> {
    return apiClient.post<EnrollStudentResponse>('/students/enroll', payload);
  }

  async listStudents(params?: {
    page?: number; limit?: number; affiliateId?: string; status?: string;
  }): Promise<{ students: Student[]; total: number; page: number; limit: number }> {
    const qs = new URLSearchParams();
    if (params?.page)        qs.set('page',        String(params.page));
    if (params?.limit)       qs.set('limit',       String(params.limit));
    if (params?.affiliateId) qs.set('affiliateId', params.affiliateId);
    if (params?.status)      qs.set('status',      params.status);
    return apiClient.get(`/students?${qs}`);
  }

  async getStudent(id: string): Promise<Student> {
    return apiClient.get<Student>(`/students/${id}`);
  }

  async updateStudent(id: string, data: Partial<EnrollStudentPayload>): Promise<Student> {
    return apiClient.patch<Student>(`/students/${id}`, data);
  }

  async uploadPhoto(id: string, file: File): Promise<{ photoUrl: string }> {
    const formData = new FormData();
    formData.append('photo', file);
    return apiClient.post<{ photoUrl: string }>(
      `/students/${id}/upload-photo`, formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  }

  async getCertificates(studentId: string): Promise<Certificate[]> {
    return apiClient.get<Certificate[]>(`/students/${studentId}/certificates`);
  }

  async downloadCertificate(certificateId: string): Promise<Blob> {
    return apiClient.get(`/certificates/${certificateId}/download`, { responseType: 'blob' }) as unknown as Blob;
  }

  async requestDiploma(studentId: string, data: { courseId: string; requestType: 'original' | 'second_copy' }): Promise<DiplomaRequest> {
    return apiClient.post<DiplomaRequest>(`/students/${studentId}/request-diploma`, data);
  }

  async getDiplomaRequests(studentId: string): Promise<DiplomaRequest[]> {
    return apiClient.get<DiplomaRequest[]>(`/students/${studentId}/diploma-requests`);
  }

  async getCarteiraDigital(studentId: string): Promise<CarteiraDigital> {
    return apiClient.get<CarteiraDigital>(`/students/${studentId}/carteira`);
  }

  async generateCarteiraDigital(studentId: string): Promise<CarteiraDigital> {
    return apiClient.post<CarteiraDigital>(`/students/${studentId}/carteira`);
  }

  async downloadCarteira(studentId: string): Promise<Blob> {
    return apiClient.get(`/students/${studentId}/carteira/download`, { responseType: 'blob' }) as unknown as Blob;
  }

  async getCourses(studentId: string): Promise<StudentCourse[]> {
    return apiClient.get<StudentCourse[]>(`/students/${studentId}/courses`);
  }

  async getStats(): Promise<StudentStats> {
    return apiClient.get<StudentStats>('/students/stats/summary');
  }

  async findBySystemId(systemId: string): Promise<Student | null> {
    try { return await apiClient.get<Student>(`/students/system/${systemId}`); }
    catch { return null; }
  }
}

export const studentService = new StudentService();
export default studentService;